import {Injectable, signal, WritableSignal} from '@angular/core';
import {LanguageService} from 'app/shared/services/language.service';
import {filter, map, Observable, of, switchMap, tap} from 'rxjs';
import {API_MAP} from '../helpers/api.map';
import {Footer} from '../models/footer.model';
import {Header} from '../models/header.model';
import {SharedContent} from '../models/shared-content.model';
import {BaseDataService} from './base-data.service';
import {NavigationEnd, Router} from '@angular/router';
import {containerTypes, languages} from '../../app.routes';

interface SharedData {
  hospitalName: string;
  language: string;
  data: SharedContent;
}

@Injectable({ providedIn: 'root' })
export class SharedService extends BaseDataService<any> {
  private sharedData = signal<SharedData[]>([]);
  public header: WritableSignal<Header> = signal<Header>({} as Header);
  public footer: WritableSignal<Footer> = signal<Footer>({} as Footer);
  lightMode = signal(true);

  constructor(private languageService: LanguageService,
              private router: Router
  ) {
    super();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        switchMap((event: NavigationEnd) => {
          const url = event.urlAfterRedirects;
          return this.get(url);
        })
      )
      .subscribe();
  }


  override get(url: string): Observable<SharedContent> {
    const urlSegments = url.split('/').filter(segment => segment);
    const language = languages.find(l => l.ContainerName.toLowerCase() === urlSegments[0])?.ContainerName.toLowerCase() || '';
    let hospitalName = language.length > 0 ? urlSegments[1] : urlSegments[0];
    if(!containerTypes.find(c=>c.ContainerUrl.startsWith(`/${hospitalName}`)  && c.ContainerAlias == "hospital")){
      hospitalName = "";
    }
    const cacheData = this.sharedData().find(d => d.hospitalName === hospitalName && d.language === language);

    if (cacheData) {
      this.initSharedData(cacheData.data, language);
      return of(cacheData.data);
    }
    return super.get(`${API_MAP.shared(language)}${hospitalName ? '?hospitalPath=' + hospitalName : ''}`).pipe(
      switchMap((data: SharedContent) => {
        if (!data.header || !data.footer) {
          return super.get(API_MAP.shared(language)).pipe(
            map((globalShared: SharedContent) => {
              return {
                footer: data.footer ?? globalShared.footer,
                header: data.header ?? globalShared.header,
                languages: data.languages
              } as SharedContent;
            })
          )
        }
        return of(data);
      }),
      tap((data: SharedContent) => {
        this.initSharedData(data, language,hospitalName);
        this.sharedData.update(currentArray => [...currentArray, { hospitalName, language, data }]);
      }),
    );
  }

  initSharedData(d: SharedContent, lang?: string,hospitalName?:string) {
    if (d.header && hospitalName) {
      d.header.backgroundColor = d.header.backgroundColor ?? '293284';
      d.header.colorRainbow1 = d.header.colorRainbow1 ?? 'EA3D85';
      d.header.colorRainbow2 = d.header.colorRainbow2 ?? '10BF9C';
    }
    this.header.set(d.header);
    this.footer.set(d.footer);
    if (!this.languageService.currentLanguage().prefix || this.languageService.currentLanguage().prefix !== lang) {
      this.languageService.init(d.languages, lang);
    }
  }

  private setLightMode(value: boolean): void {
    this.lightMode.set(value);
  }

  enableLightMode(): void {
    this.setLightMode(true);
  }

  disableLightMode(): void {
    this.setLightMode(false);
  }
}
